// Tab-local, in-memory company context + dispatch-time fetch adapter (ADMINISTRATION-DESIGN.md
// §4 [M3], [C1]). No localStorage/sessionStorage/cookies/cross-tab sync — module memory only,
// one instance per tab. The fetch adapter reads this store at dispatch time (not client-creation
// time) so a request always carries the company that was active when it was issued, and a later
// switch cannot retroactively change an in-flight request.

let activeCompanyId: string | null = null;
let generation = 0;
let generationController = new AbortController();

export function getActiveCompanyId(): string | null {
  return activeCompanyId;
}

export function getCompanyContextGeneration(): number {
  return generation;
}

function rotateGeneration(): void {
  generation += 1;
  const previous = generationController;
  generationController = new AbortController();
  previous.abort();
}

export function setActiveCompanyId(id: string): void {
  if (id === activeCompanyId) return;
  activeCompanyId = id;
  rotateGeneration();
}

export function clearActiveCompanyId(): void {
  if (activeCompanyId === null) return;
  activeCompanyId = null;
  rotateGeneration();
}

function resolveHeaders(input: RequestInfo | URL, init: RequestInit | undefined): Headers {
  if (init?.headers !== undefined) return new Headers(init.headers);
  if (typeof Request !== 'undefined' && input instanceof Request) return new Headers(input.headers);
  return new Headers();
}

function resolveCallerSignal(input: RequestInfo | URL, init: RequestInit | undefined): AbortSignal | undefined {
  if (init?.signal != null) return init.signal;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.signal ?? undefined;
  return undefined;
}

// Composes the caller's AbortSignal (if any) with the current company-context generation signal,
// captured at dispatch time. Either aborting cancels the underlying request; listeners are always
// removed once the request settles.
export function createCompanyScopedFetch(baseFetch: typeof fetch = fetch): typeof fetch {
  return function companyScopedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const headers = resolveHeaders(input, init);
    const companyId = activeCompanyId;
    if (companyId !== null) {
      headers.set('x-company-id', companyId);
    }

    const callerSignal = resolveCallerSignal(input, init);
    const genSignal = generationController.signal;
    const composed = new AbortController();

    const abortFromCaller = () => composed.abort(callerSignal?.reason);
    const abortFromGeneration = () => composed.abort(genSignal.reason);

    if (callerSignal?.aborted === true) {
      abortFromCaller();
    } else if (genSignal.aborted) {
      abortFromGeneration();
    } else {
      callerSignal?.addEventListener('abort', abortFromCaller);
      genSignal.addEventListener('abort', abortFromGeneration);
    }

    const cleanup = () => {
      callerSignal?.removeEventListener('abort', abortFromCaller);
      genSignal.removeEventListener('abort', abortFromGeneration);
    };

    const requestInit: RequestInit = { ...init, headers, signal: composed.signal };
    return baseFetch(input, requestInit).finally(cleanup);
  };
}
